import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { ScrollLockGuardian } from "@/components/ScrollLockGuardian";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { UtmCapture } from "@/components/UtmCapture";
import { PostAuthNoticeListener } from "@/components/PostAuthNoticeListener";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { RouteFallback } from "@/components/RouteFallback";
import { useAuthStateListener } from "@/hooks/useAuthStateListener";
const PFALayout = lazy(() =>
  import("@/components/layouts/PFALayout").then((module) => ({ default: module.PFALayout })),
);
const PfaBrowseShell = lazy(() =>
  import("@/components/layouts/PfaBrowseShell").then((module) => ({ default: module.PfaBrowseShell })),
);
const CatererLayout = lazy(() =>
  import("@/components/layouts/CatererLayout").then((module) => ({ default: module.CatererLayout })),
);
const AdminLayout = lazy(() =>
  import("@/components/layouts/AdminLayout").then((module) => ({ default: module.AdminLayout })),
);
const TesterLayout = lazy(() =>
  import("@/features/tester/TesterLayout").then((module) => ({ default: module.TesterLayout })),
);
const TesterOnboardingGate = lazy(() =>
  import("@/features/tester/TesterOnboardingGate").then((module) => ({
    default: module.TesterOnboardingGate,
  })),
);
const TesterIntroPage = lazy(() => import("./pages/tester/TesterIntroPage"));
const TesterQuizPage = lazy(() => import("./pages/tester/TesterQuizPage"));
const TesterSuitesPage = lazy(() => import("./pages/tester/TesterSuitesPage"));
const TesterSuiteDetailPage = lazy(() => import("./pages/tester/TesterSuiteDetailPage"));
const TesterStepPage = lazy(() => import("./pages/tester/TesterStepPage"));
const TesterBugsPage = lazy(() => import("./pages/tester/TesterBugsPage"));
const TesterBugNewPage = lazy(() => import("./pages/tester/TesterBugNewPage"));
const TesterInviteClaimPage = lazy(() => import("./pages/tester/TesterInviteClaimPage"));
import { CommunityRouteGuard } from "@/components/pfa/CommunityRouteGuard";
/** Eager import so `/` never shows the Suspense fallback (avoids large CLS when chunk swaps to full layout). */
import Landing from "./pages/Landing";
const FlightAttendantsMarketing = lazy(() => import("./pages/FlightAttendantsMarketing"));
const ForCaterersMarketing = lazy(() => import("./pages/ForCaterersMarketing"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Faq = lazy(() => import("./pages/Faq"));
const Auth = lazy(() => import("./pages/Auth"));
const AuthIndexRedirect = lazy(() => import("./pages/AuthIndexRedirect"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const PendingApproval = lazy(() => import("./pages/PendingApproval"));
const Banned = lazy(() => import("./pages/Banned"));
const PFAHome = lazy(() => import("./pages/pfa/Home"));
const PFAMyProfile = lazy(() => import("./pages/pfa/MyProfile"));
const EnablePfaAccount = lazy(() => import("./pages/pfa/EnablePfaAccount"));
const PFAAccount = lazy(() => import("./pages/pfa/Account"));
const SearchCaterers = lazy(() => import("./pages/pfa/SearchCaterers"));
const AirportCaterersBrowseGate = lazy(() => import("./pages/pfa/AirportCaterersBrowseGate"));
const CatererBrowseGate = lazy(() => import("./pages/pfa/CatererBrowseGate"));
const SeoAirportPage = lazy(() => import("./pages/seo/SeoAirportPage"));
const SeoCatererPage = lazy(() => import("./pages/seo/SeoCatererPage"));
const Favourites = lazy(() => import("./pages/pfa/Favourites"));
const SharedFavourites = lazy(() => import("./pages/public/SharedFavourites"));
const Checkout = lazy(() => import("./pages/pfa/Checkout"));
const PFAMessages = lazy(() => import("./pages/pfa/Messages"));
const CommunityPage = lazy(() => import("./pages/pfa/Community"));
const CommunityPostPage = lazy(() => import("./pages/pfa/CommunityPost"));
const PeerMessagesPage = lazy(() => import("./pages/pfa/PeerMessages"));
const PFAResubmitProfile = lazy(() => import("./pages/pfa/ResubmitProfile"));
const JoinReferral = lazy(() => import("./pages/JoinReferral"));
const CatererHome = lazy(() => import("./pages/caterer/Home"));
const CatererMyProfile = lazy(() => import("./pages/caterer/MyProfile"));
const CatererAccount = lazy(() => import("./pages/caterer/Account"));
const CatererMessages = lazy(() => import("./pages/caterer/Messages"));
const CatererAnalytics = lazy(() => import("./pages/caterer/Analytics"));
const CatererTeam = lazy(() => import("./pages/caterer/Team"));
const CatererAirports = lazy(() => import("./pages/caterer/Airports"));
const CatererTeamAccept = lazy(() => import("./pages/caterer/TeamAccept"));
const CatererResubmitProfile = lazy(() => import("./pages/caterer/ResubmitProfile"));
const StripeConnect = lazy(() => import("./pages/StripeConnect"));
const StripeReturn = lazy(() => import("./pages/StripeReturn"));
const StripeRefresh = lazy(() => import("./pages/StripeRefresh"));
const StripeOAuthCallback = lazy(() => import("./pages/StripeOAuthCallback"));
const CatererClaim = lazy(() => import("./pages/CatererClaim"));
const CatererInviteAuth = lazy(() => import("./pages/CatererInviteAuth"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const ProvisionedCaterers = lazy(() => import("./pages/admin/ProvisionedCaterers"));
const AdminTestersPage = lazy(() => import("./pages/admin/Testers"));
const AdminAccount = lazy(() => import("./pages/admin/Account"));
const AdminBlog = lazy(() => import("./pages/admin/Blog"));
const ImpersonateCallback = lazy(() => import("./pages/admin/ImpersonateCallback"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const CatererSetPassword = lazy(() => import("./pages/CatererSetPassword"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));

function MarketplaceAirportRedirect() {
  const { airportId } = useParams<{ airportId: string }>();
  return <Navigate to={`/CFA/search-caterers/${airportId ?? ""}`} replace />;
}

function MarketplaceCatererRedirect() {
  const { catererId } = useParams<{ catererId: string }>();
  return <Navigate to={`/CFA/caterers/${catererId ?? ""}`} replace />;
}

function ShareFavouritesRedirect() {
  const { shareId } = useParams<{ shareId: string }>();
  return <Navigate to={`/CFA/favourites/share/${shareId ?? ""}`} replace />;
}

/** Legacy bookmarks and emails may still use /PFA/… — keep redirects permanent. */
function LegacyPfaPathRedirect() {
  const location = useLocation();
  const target = `${location.pathname.replace(/^\/PFA(?=\/|$)/, "/CFA")}${location.search}${location.hash}`;
  return <Navigate to={target} replace />;
}

const AppRoutes = () => {
  useAuthStateListener();

  useEffect(() => {
    document.getElementById("lcp-hero-prefetch")?.remove();
  }, []);

  return (
    <>
      <ScrollLockGuardian />
      <UtmCapture />
      <PostAuthNoticeListener />
      <CookieConsentBanner />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/landing" element={<Navigate to="/" replace />} />
        <Route path="/flight-attendants" element={<FlightAttendantsMarketing />} />
        <Route path="/for-caterers" element={<ForCaterersMarketing />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/faq" element={<Faq />} />
        <Route path="/airports/:slug" element={<SeoAirportPage />} />
        <Route path="/caterers/:slug" element={<SeoCatererPage />} />
        <Route path="/auth" element={<AuthIndexRedirect />} />
        <Route path="/auth/signin" element={<Auth />} />
        <Route path="/auth/signup" element={<Auth />} />
        <Route path="/auth/forgot" element={<Auth />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/join" element={<JoinReferral />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/caterer/claim" element={<CatererClaim />} />
        <Route path="/tester/invite" element={<TesterInviteClaimPage />} />
        <Route path="/caterer/team/accept" element={<CatererTeamAccept />} />
        <Route path="/caterer/invite-auth" element={<CatererInviteAuth />} />
        <Route path="/admin/impersonate-callback" element={<ImpersonateCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/caterer/set-password" element={<CatererSetPassword />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/pending-approval" element={<PendingApproval />} />
        <Route path="/banned" element={<Banned />} />
        <Route path="/marketplace/search-caterers" element={<Navigate to="/CFA/search-caterers" replace />} />
        <Route
          path="/marketplace/search-caterers/:airportId"
          element={<MarketplaceAirportRedirect />}
        />
        <Route
          path="/marketplace/caterers/:catererId"
          element={<MarketplaceCatererRedirect />}
        />
        <Route path="/share/favourites/:shareId" element={<ShareFavouritesRedirect />} />
        <Route path="/PFA/*" element={<LegacyPfaPathRedirect />} />

        <Route path="/CFA">
          <Route
            element={
              <Suspense fallback={<RouteFallback />}>
                <PfaBrowseShell />
              </Suspense>
            }
          >
            <Route path="search-caterers" element={<SearchCaterers />} />
            <Route path="search-caterers/:airportId" element={<AirportCaterersBrowseGate />} />
            <Route path="caterers/:catererId" element={<CatererBrowseGate />} />
            <Route path="checkout/guest" element={<Checkout />} />
            <Route path="favourites/share/:shareId" element={<SharedFavourites />} />
          </Route>
          <Route
            element={
              <ProtectedRoute allowedRole="pfa">
                <Suspense fallback={<RouteFallback />}>
                  <PFALayout />
                </Suspense>
              </ProtectedRoute>
            }
          >
          <Route path="home" element={<PFAHome />} />
          <Route path="myprofile" element={<PFAMyProfile />} />
          <Route path="account" element={<PFAAccount />} />
          <Route path="favourites" element={<Favourites />} />
          <Route path="checkout/:basketId" element={<Checkout />} />
          <Route path="messages/:orderId?" element={<PFAMessages />} />
          <Route
            path="community"
            element={
              <CommunityRouteGuard>
                <CommunityPage />
              </CommunityRouteGuard>
            }
          />
          <Route
            path="community/:airportId"
            element={
              <CommunityRouteGuard>
                <CommunityPage />
              </CommunityRouteGuard>
            }
          />
          <Route
            path="community/post/:postId"
            element={
              <CommunityRouteGuard>
                <CommunityPostPage />
              </CommunityRouteGuard>
            }
          />
          <Route
            path="community/messages"
            element={
              <CommunityRouteGuard>
                <PeerMessagesPage />
              </CommunityRouteGuard>
            }
          />
          <Route
            path="community/messages/:conversationId"
            element={
              <CommunityRouteGuard>
                <PeerMessagesPage />
              </CommunityRouteGuard>
            }
          />
          </Route>
          <Route
            path="enable-cfa-account"
            element={
              <ProtectedRoute allowedRole="referrals">
                <Suspense fallback={<RouteFallback />}>
                  <EnablePfaAccount />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        <Route
          path="/caterer"
          element={
            <ProtectedRoute allowedRole="caterer">
              <Suspense fallback={<RouteFallback />}>
                <CatererLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route path="home" element={<CatererHome />} />
          <Route path="myprofile" element={<CatererMyProfile />} />
          <Route path="airports" element={<CatererAirports />} />
          <Route path="team" element={<CatererTeam />} />
          <Route path="account" element={<CatererAccount />} />
          <Route path="messages/:orderId?" element={<CatererMessages />} />
          <Route path="analytics" element={<CatererAnalytics />} />
        </Route>

        <Route
          path="/tester"
          element={
            <ProtectedRoute allowedRole="tester">
              <Suspense fallback={<RouteFallback />}>
                <TesterLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route
            element={
              <Suspense fallback={<RouteFallback />}>
                <TesterOnboardingGate />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="/tester/suites" replace />} />
            <Route path="intro" element={<TesterIntroPage />} />
            <Route path="quiz" element={<TesterQuizPage />} />
            <Route path="suites" element={<TesterSuitesPage />} />
            <Route path="suites/:slug" element={<TesterSuiteDetailPage />} />
            <Route path="suites/:slug/steps/:stepKey" element={<TesterStepPage />} />
            <Route path="bugs" element={<TesterBugsPage />} />
            <Route path="bugs/new" element={<TesterBugNewPage />} />
          </Route>
        </Route>

        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRole="admin">
              <Suspense fallback={<RouteFallback />}>
                <AdminLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="traiteurs" element={<ProvisionedCaterers />} />
          <Route path="testers" element={<AdminTestersPage />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="aeroports" element={<Navigate to="/admin/dashboard?tab=aeroports" replace />} />
          <Route path="account" element={<AdminAccount />} />
        </Route>

        <Route path="/caterer/stripe-connect" element={<StripeConnect />} />
        <Route path="/caterer/stripe-oauth-callback" element={<StripeOAuthCallback />} />
        <Route path="/caterer/stripe-return" element={<StripeReturn />} />
        <Route path="/caterer/stripe-refresh" element={<StripeRefresh />} />

        <Route path="/CFA/resubmit" element={<PFAResubmitProfile />} />
        <Route path="/caterer/resubmit" element={<CatererResubmitProfile />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </>
  );
};

/**
 * Data router is required for `useBlocker` (basket leave-guard on CatererDetail).
 * Nested `<Routes>` in AppRoutes keep the existing route tree without a full rewrite.
 */
const router = createBrowserRouter(createRoutesFromElements(<Route path="*" element={<AppRoutes />} />));

const App = () => (
  <AppErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <RouterProvider router={router} />
      </TooltipProvider>
    </QueryClientProvider>
  </AppErrorBoundary>
);

export default App;
